export const BRAND = { green: "#087F5B", greenDark: "#066548", navy: "#0F1F38", mint: "#EAF8F3", text: "#132238", sub: "#667085", border: "#E5E9EE" };

export const Chip = ({ selected, onClick, children, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`sf-chip px-3.5 py-2 rounded-full text-sm font-medium border transition-colors duration-150 active:scale-95 ${
      selected
        ? "bg-[#087F5B] text-white border-[#087F5B]"
        : "bg-white text-[#667085] border-[#E5E9EE] hover:bg-slate-50 hover:text-[#132238]"
    }`}
  >
    {children}
  </button>
);

export const ChipGroup = ({ label, options, value, onChange, multi = false, testPrefix }) => {
  const toggle = (opt) => {
    if (multi) {
      onChange(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt]);
    } else onChange(opt);
  };
  return (
    <div className="space-y-2">
      <p className="text-[13px] font-semibold text-[#132238]">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <Chip
            key={opt}
            testId={`${testPrefix}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            selected={multi ? value.includes(opt) : value === opt}
            onClick={() => toggle(opt)}
          >
            {opt}
          </Chip>
        ))}
      </div>
    </div>
  );
};

export const Section = ({ title, children, action, subtitle }) => (
  <div className="bg-white border border-[#E5E9EE] rounded-2xl p-4 sm:p-6 shadow-[0_1px_2px_rgba(15,31,56,0.04)]">
    <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
      <div>
        <h3 className="font-heading text-[17px] font-semibold text-[#132238] tracking-tight">{title}</h3>
        {subtitle && <p className="text-xs text-[#667085] mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
    {children}
  </div>
);

export const StatusDot = ({ color }) => {
  const map = { green: "bg-[#087F5B]", gray: "bg-slate-300", amber: "bg-amber-500", red: "bg-red-500", blue: "bg-blue-500" };
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${map[color] || "bg-slate-300"}`} />;
};

export const LevelBadge = ({ level }) => {
  const map = {
    routine: "bg-slate-100 text-slate-600 border-slate-200",
    elevated: "bg-amber-50 text-amber-700 border-amber-200",
    critical: "bg-red-50 text-red-700 border-red-200",
  };
  return <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${map[level]}`}>{level}</span>;
};

export const StatusBadge = ({ tone = "info", children, testId }) => {
  const map = {
    success: "bg-[#EAF8F3] text-[#087F5B] border-[#C8EFE2]",
    warning: "bg-amber-50 text-amber-700 border-amber-200",
    critical: "bg-red-50 text-red-700 border-red-200",
    info: "bg-blue-50 text-blue-700 border-blue-200",
    neutral: "bg-slate-50 text-[#667085] border-[#E5E9EE]",
  };
  return <span data-testid={testId} className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${map[tone]}`}>{children}</span>;
};

export const Avatar = ({ name, size = "md", tone = "green" }) => {
  const initials = (name || "?").split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  const sizes = { xs: "w-6 h-6 text-[9px]", sm: "w-8 h-8 text-[10px]", md: "w-9 h-9 text-xs", lg: "w-11 h-11 text-sm" };
  const tones = { green: "bg-[#087F5B] text-white", navy: "bg-[#0F1F38] text-white", mint: "bg-[#EAF8F3] text-[#087F5B]", blue: "bg-blue-100 text-blue-700", slate: "bg-slate-100 text-slate-600" };
  return <span className={`rounded-full flex items-center justify-center font-bold shrink-0 ${sizes[size]} ${tones[tone]}`}>{initials}</span>;
};

export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="fade-up flex items-start justify-between flex-wrap gap-3">
    <div>
      <h1 className="font-heading text-[28px] sm:text-[32px] font-bold tracking-tight text-[#132238] leading-tight">{title}</h1>
      {subtitle && <p className="text-sm text-[#667085] mt-1">{subtitle}</p>}
    </div>
    {actions && <div className="flex gap-2 flex-wrap items-center">{actions}</div>}
  </div>
);

export const KpiCard = ({ label, value, sub, icon: Icon, tone = "green", trend, ring, testId }) => {
  const iconTones = {
    green: "bg-[#EAF8F3] text-[#087F5B]",
    blue: "bg-blue-50 text-blue-600",
    amber: "bg-amber-50 text-amber-600",
    red: "bg-red-50 text-red-500",
    navy: "bg-slate-100 text-[#0F1F38]",
  };
  return (
    <div data-testid={testId} className="sf-card-hover bg-white border border-[#E5E9EE] rounded-2xl p-4 sm:p-5 shadow-[0_1px_2px_rgba(15,31,56,0.04)]">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[13px] font-medium text-[#667085]">{label}</p>
          <p className="font-heading text-[26px] sm:text-[30px] font-bold tracking-tight text-[#132238] mt-1 leading-none">{value}</p>
          {sub && <p className="text-xs text-[#667085] mt-2 flex items-center gap-1">{trend && <span className={`font-semibold ${trend === "up" ? "text-[#087F5B]" : trend === "down" ? "text-red-500" : "text-[#667085]"}`}>{trend === "up" ? "↑" : trend === "down" ? "↓" : ""}</span>}{sub}</p>}
        </div>
        {ring != null ? (
          <RingProgress value={ring} />
        ) : Icon ? (
          <span className={`p-2.5 rounded-xl ${iconTones[tone]} shrink-0`}>
            <Icon size={18} />
          </span>
        ) : null}
      </div>
    </div>
  );
};

export const RingProgress = ({ value, size = 46 }) => {
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value || 0));
  return (
    <svg width={size} height={size} className="shrink-0 -rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E5E9EE" strokeWidth="5" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#087F5B" strokeWidth="5" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c - (pct / 100) * c} style={{ transition: "stroke-dashoffset 0.8s cubic-bezier(0.16,1,0.3,1)" }} />
    </svg>
  );
};

export const ProgressBar = ({ value, tone = "green", height = "h-2" }) => {
  const tones = { green: "bg-[#087F5B]", amber: "bg-amber-500", red: "bg-red-500", blue: "bg-blue-500" };
  return (
    <div className={`${height} bg-slate-100 rounded-full overflow-hidden`}>
      <div className={`h-full rounded-full ${tones[tone]}`} style={{ width: `${Math.max(0, Math.min(100, value || 0))}%`, transition: "width 0.7s cubic-bezier(0.16,1,0.3,1)" }} />
    </div>
  );
};

export const StatCard = ({ label, value, sub, icon: Icon, tone = "default", testId }) => {
  const toneMap = { default: "navy", good: "green", warn: "amber", bad: "red" };
  return <KpiCard testId={testId} label={label} value={value} sub={sub} icon={Icon} tone={toneMap[tone] || "navy"} />;
};

export const EmptyState = ({ icon: Icon, title, hint, action }) => (
  <div className="flex flex-col items-center justify-center text-center py-10 px-4">
    {Icon && (
      <span className="w-12 h-12 rounded-2xl bg-[#EAF8F3] text-[#087F5B] flex items-center justify-center mb-3">
        <Icon size={22} />
      </span>
    )}
    <p className="font-heading text-[15px] font-semibold text-[#132238]">{title}</p>
    {hint && <p className="text-[13px] text-[#667085] mt-1 max-w-sm leading-relaxed">{hint}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const PageSkeleton = () => (
  <div className="space-y-4">
    <div className="sf-skeleton h-9 w-56" />
    <div className="sf-skeleton h-4 w-80" />
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 pt-2">
      <div className="sf-skeleton h-24" />
      <div className="sf-skeleton h-24" />
      <div className="sf-skeleton h-24" />
      <div className="sf-skeleton h-24" />
    </div>
    <div className="sf-skeleton h-72" />
  </div>
);
